<template>
  <div>
    <div class="container">
      <form @submit.prevent="submitInputsProfile" autocomplete="off" novalidate>
        <heading-one class="-margin-md">お客さま情報のご入力</heading-one>

        <errors-alert :errors="errors" column-only="true" :items="inputItemsEn"></errors-alert>

        <wizard-group>
          <main-wizard :active-index="0" type="entry"></main-wizard>
          <sub-wizard :active-index="0" type="usernew"></sub-wizard>
        </wizard-group>

        <table-group>
          <heading-two>お客さま情報</heading-two>

          <div class="_mb-10">
            <small class="note important">※氏名（漢字、フリガナ）などに誤りが無いか、必ずご確認ください。</small>
            <small class="note">※必須の項目は必ずご入力ください。</small>
          </div>

          <table-box class="-entry">
            <table-body>
              <table-row>
                <template v-if="showNameKanjiLabel">
                  <table-header badge="必須" badge-size="-sm">
                    氏名<small>（漢字）</small>
                  </table-header>
                  <table-data :hasError="errors['name_kanji']">
                    <template v-if="userData.name_kanji">
                      <label>{{userData.name_kanji}}</label>
                    </template>
                  </table-data>
                </template>
                <template v-else>
                  <table-header badge="必須" badge-size="-sm">
                    氏名<small>（漢字）<small class="moji">【全角】</small></small>
                  </table-header>
                  <table-data :hasError="errors['name_kanji']">
                    <inputgroup-append class="name-box _mb-15">
                      <input-group class="name" :errors="errors" :propNames="['name_kanji']">
                        <div class="input-box _pr-sm-10 _mr-md-40">
                          <input-item v-model="userData.lastname_kanji" class="-sm" id="lastname_kanji" :maxlength="13" tracking-name="en002_lastname_kanji"></input-item>
                          <input-label>
                            例：英雄
                          </input-label>
                        </div>
                        <div class="input-box _pl-sm-10">
                          <input-item v-model="userData.firstname_kanji" class="-sm" id="firstname_kanji" :maxlength="13" tracking-name="en002_firstname_kanji"></input-item>
                          <input-label>
                            例：太郎
                          </input-label>
                        </div>
                      </input-group>
                    </inputgroup-append>
                    <small class="warning">※左の欄に姓、右の欄に名が入るように訂正をお願い致します。</small>
                  </table-data>
                </template>
              </table-row>
              <table-row>
                <template v-if="showNameKanaLabel">
                  <table-header badge="必須" badge-size="-sm">
                    氏名<small>（フリガナ）</small>
                  </table-header>
                  <table-data :hasError="errors['name_kana']">
                    <template v-if="userData.name_kana">
                      <label>{{userData.name_kana}}</label>
                    </template>
                  </table-data>
                </template>
                <template v-else>
                  <table-header badge="必須" badge-size="-sm">
                    氏名<small>（フリガナ）<small class="moji">【全角カナ】</small></small>
                  </table-header>
                  <table-data :hasError="errors['name_kana']">
                    <inputgroup-append class="name-box _mb-15">
                      <input-group class="name _mr-md-40" :errors="errors" :propNames="['name_kana']">
                        <div class="input-box _pr-sm-10 _mr-md-40">
                          <input-item v-model="userData.lastname_kana" class="-sm" id="lastname_kana" :maxlength="17" tracking-name="en002_lastname_kana"></input-item>
                          <input-label>
                            例：エイユウ
                          </input-label>
                        </div>
                        <div class="input-box _pl-sm-10">
                          <input-item v-model="userData.firstname_kana" class="-sm" id="firstname_kana" :maxlength="17" tracking-name="en002_firstname_kana"></input-item>
                          <input-label>
                            例：タロウ
                          </input-label>
                        </div>
                      </input-group>
                    </inputgroup-append>
                    <small class="warning">※左の欄に姓、右の欄に名が入るように訂正をお願い致します。</small>
                  </table-data>
                </template>
              </table-row>
              <table-row>
                <table-header badge="必須" badge-size="-sm">
                  生年月日<small class="moji" v-if="!hasBth">【半角数字】</small>
                </table-header>
                <table-data :hasError="errors['bth']">
                  <template v-if="hasBth">
                    <label>{{bthYyyy}}年 {{bthMm}}月 {{bthDd}}日</label>
                  </template>
                  <template v-else>
                    <input-group :errors="errors" :propNames="['bth']">
                      <input-item v-model="userData.bth" class="-sm" id="bth" type="text" maxlength="8" tracking-name="en002_bth"></input-item>
                      <input-label>
                        入力例：1988年1月1日 → 19880101
                      </input-label>
                    </input-group>
                  </template>
                </table-data>
              </table-row>
              <table-row>
                <table-header badge="必須" badge-size="-sm">
                  性別
                </table-header>
                <table-data :hasError="errors['sex']">
                  <template v-if="hasSex">
                    {{displaySex}}
                  </template>
                  <template v-else>
                    <input-group :errors="errors" :propNames="['sex']">
                      <btngroup-radio>
                        <btn-radio v-for="(sexType, index) in sexTypes" v-model="userData.sex"
                                   :key="'sexType' + index" :id="'sexType' + index" name="sexTypeRadio"
                                   :value="sexType['value']" :label="sexType['name']" :tracking-name="`en002_sex_type_${index}`">
                        </btn-radio>
                      </btngroup-radio>
                    </input-group>
                  </template>
                </table-data>
              </table-row>
              <table-row v-if="!!hasPhoneAu">
                <table-header badge="必須" badge-size="-sm">
                  au携帯電話番号または<br>UQ mobile 電話番号
                </table-header>
                <table-data :hasError="errors['phone_au_1']">
                  <input-group :errors="errors" :propNames="['phone_au_1']">
                    <div id="phoneNumber1">{{userData.phone_au_1}}-{{userData.phone_au_2}}-{{userData.phone_au_3}}</div>
                  </input-group>
                </table-data>
              </table-row>

              <table-row v-else>
                <table-header badge="必須" badge-size="-sm">電話番号<small>（その他）<small class="moji">【半角数字】</small></small></table-header>
                <table-data :hasError="errors['phone_mobile']">
                  <input-group :errors="errors" :propNames="['phone_mobile']">
                    <inputgroup-append>
                      <input-phone :phone="{phone1: userData.phone_mobile_1, phone2: userData.phone_mobile_2, phone3: userData.phone_mobile_3}" id="phone_mobile" @input="setPhoneMobile" tracking-name="en002_phone_mobile"></input-phone>
                    </inputgroup-append>
                    <input-label>
                      入力例： 090-****-****
                    </input-label>
                  </input-group>
                </table-data>
              </table-row>

              <template v-if="showDefaultMail">
                <table-row>
                  <table-header badge="必須" badge-size="-sm" rowspan="3">
                    メールアドレス
                    <btn-tooltip slot="tooltip" target="mailAddressTooltip" class="-question"></btn-tooltip>
                    <tooltips-item slot="tooltip" target="mailAddressTooltip" placement="righttop" clazz="question-box">
                      au IDにご登録の連絡先メールアドレスを表示しております。変更をご希望の場合は、お申し込み完了後にau IDサイトにて連絡先メールアドレスをご登録ください。
                    </tooltips-item>
                  </table-header>
                  <table-data id="emailAddress1">
                    {{displayEzMail}}
                  </table-data>
                </table-row>
                <table-row>
                  <table-data id="emailAddress2">
                    {{displayMail1}}
                  </table-data>
                </table-row>
                <table-row>
                  <table-data id="emailAddress3">
                    {{displayMail2}}
                  </table-data>
                </table-row>
              </template>

              <template v-if="showLoanMail">
                <table-row>
                  <table-header badge="必須" badge-size="-sm">
                    メールアドレス<small class="moji">【半角英数】</small>
                  </table-header>
                  <table-data :hasError="errors['loan_mail']">
                    <input-group :errors="errors" :propNames="['loan_mail']">
                      <input-item v-model="userData.loan_mail" class="-lg" id="loan_mail" :maxlength="50" tracking-name="en002_loan_mail"></input-item>
                      <input-label>
                        入力例：****@au.com
                      </input-label>
                    </input-group>
                  </table-data>
                </table-row>

                <table-row>
                  <table-header badge="必須" badge-size="-sm">
                    メールアドレス<small>（確認用再入力）<small class="moji">【半角英数】</small></small>
                  </table-header>
                  <table-data :hasError="errors['loan_mail_confirmation']">
                    <input-group :errors="errors" :propNames="['loan_mail_confirmation']">
                      <input-item v-model="userData.loan_mail_confirmation" class="-lg" id="loan_mail_confirmation" :maxlength="50" tracking-name="en002_loan_mail_confirmation"></input-item>
                      <input-label>
                        入力例：****@au.com
                      </input-label>
                    </input-group>
                  </table-data>
                </table-row>
              </template>

              <table-row>
                <table-header class="-hastooltip">
                  キャンペーンメール受信
                  <btn-tooltip slot="tooltip" target="mailNoticeTooltip" class="-question"></btn-tooltip>
                  <tooltips-item slot="tooltip" target="mailNoticeTooltip" placement="righttop">
                    ご不要の場合はチェックを外してください。なお、大切なご案内に関するメールは、チェックを外した場合でも全てのお客さまに配信されます。
                  </tooltips-item>
                </table-header>
                <table-data>
                  <input-group>
                    <input-check v-model="userData.notice_flg" id="notice_flg" value="true" tracking-name="en002_notice_flg">同意する</input-check>
                  </input-group>
                </table-data>
              </table-row>

              <table-row>
                <table-header badge="必須" badge-size="-sm">
                  暗証番号<small class="moji">【半角数字4文字】</small>
                </table-header>
                <table-data :hasError="errors['pincode']">
                  <p class="pincode-txt">暗証番号を<span class="important">4桁の数字</span>で設定ください。今後のお手続き等にご利用いただきます。<br class="-spnone">生年月日、電話番号、「0000」などの4桁の同じ数字はご利用いただけません。</p>
                  <input-check v-model="showPinCode" id="pincode_flg" value="true" class="pincode-flg" tracking-name="en002_pincode_flg">暗証番号を表示する</input-check>
                  <input-group :errors="errors" :propNames="['pincode']">
                    <input-item v-model="userData.pincode" class="-sm" id="pincode" :type="pinCodeInputType" :maxlength="4" tracking-name="en002_pincode"></input-item>
                    <input-label>
                    </input-label>
                    <input-item v-model="userData.pincode_confirmation" class="-sm pincode-confirmation" id="pincode_confirmation" :type="pinCodeInputType" :maxlength="4" tracking-name="en002_pincode_confirmation"></input-item><small class="pincode-confirmation-note">（確認用再入力）</small>
                    <input-label>
                    </input-label>
                  </input-group>
                </table-data>
              </table-row>

              <table-row>
                <table-header badge="必須" badge-size="-sm">運転免許証の有無</table-header>
                <table-data :hasError="errors['driver_license_flg']">
                  <input-group :errors="errors" :propNames="['driver_license_flg']">
                    <btngroup-radio>
                      <btn-radio v-for="(driverLicenseFlg, index) in driverLicenseFlgs" v-model="userData.driver_license_flg"
                                :key="'driverLicenseFlg' + index" :id="'driverLicenseFlg' + index" name="driverLicenseFlgRadio"
                                 :value="driverLicenseFlg['value']" :label="driverLicenseFlg['name']" :tracking-name="`en002_driver_license_flg_${index}`">
                     </btn-radio>
                   </btngroup-radio>
                  </input-group>
                </table-data>
              </table-row>
              <table-row v-show="userData.driver_license_flg === '1'">
                <table-header badge="必須" badge-size="-sm">運転免許証番号<small class="moji">【半角数字】</small></table-header>
                <table-data :hasError="errors['driver_license_num']">
                  <input-group :errors="errors" :propNames="['driver_license_num']">
                    <inputgroup-append>
                      <input-item v-model="userData.driver_license_num" type="text" class="-md" id="driver_license_num" :maxlength="12" tracking-name="en002_driver_license_num"></input-item>
                    </inputgroup-append>
                    <input-label>入力例：000000000000</input-label>
                  </input-group>
                </table-data>
              </table-row>
            </table-body> <!-- table-body -->
          </table-box>
          <!--ご自宅情報-->
          <heading-two>ご自宅情報</heading-two>
          <table-box class="-entry">
            <table-body>
              <table-row>
                <table-header badge="必須" badge-size="-sm" colspan="2">
                  郵便番号<small class="moji">【半角数字※ハイフン不要】</small>
                </table-header>
                <table-data :hasError="errors['postcode']">
                  <input-group :errors="errors" :propNames="['postcode']">
                    <inputgroup-append>
                      <input-item v-model="userData.postcode" type="text" class="-md" id="postcode" :maxlength="7" tracking-name="en002_postcode"></input-item>
                    </inputgroup-append>
                    <input-label>入力例：1234567</input-label>
                  </input-group>
                </table-data>
              </table-row>
              <table-row>
                <table-header class="-harfleft" rowspan="4">
                  ご住所<small>（漢字）</small>
                </table-header>
                <table-header class="-harfright" badge="必須" badge-size="-sm">
                  都道府県<small class="moji">【全角】</small>
                </table-header>
                <table-data :hasError="errors['address_region']">
                  <input-group :errors="errors" :propNames="['address_region']">
                    <input-group>
                      <input-item v-model="userData.address_region" type="text" class="-md" id="address_region" :maxlength="60" tracking-name="en002_address_region"></input-item>
                      <input-label>入力例：東京都</input-label>
                    </input-group>
                  </input-group>
                </table-data>
              </table-row>
              <table-row>
                <table-header class="-harfright" badge="必須" badge-size="-sm">
                  市区町村<small class="moji">【全角】</small>
                </table-header>
                <table-data :hasError="errors['address_city']">
                  <input-group :errors="errors" :propNames="['address_city']">
                    <input-item v-model="userData.address_city" id="address_city" class="-lg" :maxlength="60" tracking-name="en002_address_city"></input-item>
                    <input-label>入力例：港区</input-label>
                  </input-group>
                </table-data>
              </table-row>
              <table-row>
                <table-header class="-harfright" badge="必須" badge-size="-sm">
                  地名・番地以下<small class="moji">【全角】</small>
                </table-header>
                <table-data :hasError="errors['address_street']">
                  <input-group :errors="errors" :propNames="['address_street']">
                    <small class="address-note important">※丁目、番地等は必ずご自身で記入して下さい。</small>
                    <input-item v-model="userData.address_street" id="address_street" class="-lg" :maxlength="60" tracking-name="en002_address_street"></input-item>
                    <input-label>入力例：○○○１－１－１</input-label>
                  </input-group>
                </table-data>
              </table-row>
              <table-row>
                <table-header class="-harfright">建物名・部屋番号<small class="moji">【全角】</small></table-header>
                <table-data :hasError="errors['address_building']">
                  <input-group :errors="errors" :propNames="['address_building']">
                    <input-item v-model="userData.address_building" id="address_building" class="-lg" :maxlength="60" tracking-name="en002_address_building"></input-item>
                    <input-label>入力例：○○マンション１０１号室</input-label>
                  </input-group>
                </table-data>
              </table-row>
              <table-row>
                <table-header badge="必須" badge-size="-sm" colspan="2">
                  ご住所<small>（フリガナ）<small class="moji">【全角カナ・全角英数】</small></small>
                </table-header>
                <table-data :hasError="errors['address_kana']">
                  <input-group :errors="errors" :propNames="['address_kana']">
                    <text-area v-model="userData.address_kana" id="address_kana1" class="furigana" :maxlength="166" excludeLineCode="true" tracking-name="en002_address_kana"></text-area>
                    <input-label>入力例：トウキョウトミナトク○○○１－１－１○○マンション１０１ゴウシツ<br>※スペースを空けず、つめてご入力ください。</input-label>
                  </input-group>
                </table-data>
              </table-row>
            </table-body> <!-- table-body -->
          </table-box>

          <table-box class="-entry">
            <table-body>
              <table-row>
                <table-header badge="必須" badge-size="-sm">お住まいの種類</table-header>
                <table-data :hasError="errors['home_type']">
                  <input-group :errors="errors" :propNames="['home_type']">
                    <btngroup-radio>
                      <btn-radio v-for="(homeType, index) in home_types" v-model="userData.home_type"
                                 :key="'homeType' + index" :id="'homeType' + index" name="homeTypeRadio"
                                 :value="homeType['value']" :label="homeType['name']" :class="{'-xs' : homeType['value'] === '5'}" :tracking-name="`en002_home_type_${index}`">
                      </btn-radio>
                    </btngroup-radio>
                  </input-group>
                </table-data>
              </table-row>
              <table-row>
                <table-header badge="必須" badge-size="-sm">居住年数</table-header>
                <table-data :hasError="errors['kyjy_ngt']">
                  <input-group :errors="errors" :propNames="['kyjy_ngt']">
                    <btngroup-radio>
                      <btn-radio v-for="(residenceYear, index) in residence_years" v-model="userData.nky_ngt"
                                 :key="'residenceYear' + index" :id="'residenceYear' + index" name="residenceYearRadio"
                                 :value="residenceYear['value']" :label="residenceYear['name']" :tracking-name="`en002_residence_year_${index}`">
                      </btn-radio>
                    </btngroup-radio>
                  </input-group>
                </table-data>
              </table-row>
              <table-row>
                <table-header badge="必須" badge-size="-sm" class="-hastooltip">住宅ローンもしくは家賃のお支払い<small>(月額)<small class="moji">【半角数字】</small></small>
                  <btn-tooltip slot="tooltip" target="housingLoanTooltip" class="-question"></btn-tooltip>
                  <tooltips-item slot="tooltip" target="housingLoanTooltip" placement="righttop">
                    ご自身のご負担がない場合は「0」を入力してください。
                  </tooltips-item>
                </table-header>
                <table-data :hasError="errors['rent_loan_amount']">
                  <input-group :errors="errors" :propNames="['rent_loan_amount']">
                    <inputgroup-append>
                      <input-item v-model="userData.rent_loan_amount" type="text" class="-sm" id="rentLoanAmount" :maxlength="10" tracking-name="en002_rent_loan_amount"></input-item>
                      <input-append>円／月</input-append>
                    </inputgroup-append>
                    <input-label>入力例：75000</input-label>
                  </input-group>
                </table-data>
              </table-row>
            </table-body>
          </table-box>
          <table-box class="-entry">
            <table-body>
              <table-row>
                <table-header badge="必須" badge-size="-sm">ご家族<small>（配偶者さま）</small></table-header>
                <table-data :hasError="errors['marriage_status']">
                  <input-group :errors="errors" :propNames="['marriage_status']">
                    <btngroup-radio>
                      <btn-radio v-for="(marriageStatus, index) in marriage_statuses" v-model="userData.marriage_status"
                                :key="'marriageStatus' + index" :id="'marriageStatus' + index" name="marriageStatus"
                                 :value="marriageStatus['value']" :label="marriageStatus['name']" :tracking-name="`en002_marriage_status_${index}`">
                      </btn-radio>
                   </btngroup-radio>
                  </input-group>
                </table-data>
              </table-row>
              <table-row>
                <table-header badge="必須" badge-size="-sm">ご家族<small>（お子さま）</small>
                  <btn-tooltip slot="tooltip" target="numberOfChildrenTooltip" class="-question"></btn-tooltip>
                  <tooltips-item slot="tooltip" target="numberOfChildrenTooltip" placement="righttop">
                    生計を同一とするお子さまの人数をご入力ください。
                  </tooltips-item>
                </table-header>
                <table-data :hasError="errors['number_of_children']">
                  <input-group :errors="errors" :propNames="['number_of_children']">
                    <btngroup-radio>
                      <btn-radio v-for="(numberOfChildren, index) in number_of_children" v-model="userData.number_of_children"
                                 :key="'numberOfChildren' + index" :id="'numberOfChildren' + index" name="numberOfChildren"
                                 :value="numberOfChildren['value']" :label="numberOfChildren['name']" :tracking-name="`en002_number_of_children_${index}`">
                      </btn-radio>
                    </btngroup-radio>
                  </input-group>
                </table-data>
              </table-row>
            </table-body> <!-- table-body -->
          </table-box>
        </table-group>

        <btngroup-double class="-margin-lg">
          <btn-main slot="main" class="-main" @click.prevent="submitInputsProfile" tracking-name="en002_next_button">
            お勤め先のご入力へ
          </btn-main>
          <btn-back slot="back" class="-back -sm" routerName="en001" tracking-name="en002_back_button">
            戻る
          </btn-back>
        </btngroup-double>

      </form>
    </div>
  </div>
</template>

<script>
import { initCredentials } from '../utils/Auth'
import { validateProfileLambdaInvoke, getUserDataLambdaInvoke, registDeviceInfoDataLambdaInvoke } from '../utils/LambdaHelper'
import { setTrackingEventDataSetting, getDeviceInfoData } from '../utils/TrackingDataHelper'
import { sexTypes, driverLicenseFlgs, homeTypes, residenceYears, marriageStatuses, numberOfChildren, inputItemsEn } from '../constants/constant'
import userData from '../components/mixin/UserData'
import HeadingOne from '../components/heading/HeadingOne'
import WizardGroup from '../components/wizard/WizardGroup'
import MainWizard from '../components/wizard/MainWizard'
import SubWizard from '../components/wizard/SubWizard'
import TableGroup from '../components/tables/TableGroup'
import HeadingTwo from '../components/heading/HeadingTwo'
import TableBox from '../components/tables/TableBox'
import TableBody from '../components/tables/TableBody'
import TableRow from '../components/tables/TableRow'
import TableHeader from '../components/tables/TableHeader'
import TableData from '../components/tables/TableData'
import BtngroupRadio from '../components/button/BtngroupRadio'
import BtnRadio from '../components/button/BtnRadio'
import BtnTooltip from '../components/button/BtnTooltip'
import TooltipsItem from '../components/tooltip/TooltipsItem'
import InputPhone from '../components/input/InputPhone'
import InputGroup from '../components/input/InputGroup'
import InputItem from '../components/input/InputItem'
import InputLabel from '../components/input/InputLabel'
import InputgroupAppend from '../components/input/InputgroupAppend'
import InputAppend from '../components/input/InputAppend'
import BtnInput from '../components/button/BtnInput'
import BtnSelect from '../components/button/BtnSelect'
import BtngroupDouble from '../components/button/BtngroupDouble'
import BtnMain from '../components/button/BtnMain'
import BtnBack from '../components/button/BtnBack'
import InputCheck from '../components/input/InputCheck'
import ErrorsAlert from '../components/alert/ErrorsAlert'
import TextArea from '../components/input/TextArea'

export default {
  name: 'En-002',
  metaInfo: {
    title: 'お客さま情報のご入力',
    meta: [
      {name: 'description', content: 'au PAY スマートローンはau IDをお持ちのお客様のための便利なカードレスローンサービスです。お申込みは、スマートフォン・パソコンでかんたんWEB完結！24時間お申込み可能。最短30分で審査結果をご連絡します。'},
      {name: 'keywords', content: 'au PAY スマートローン,au,カードレスローン,スマートフォン,au PAY 残高,セブン銀行スマホATM,借入,返済,手続き,入会,お客さま情報'}
    ]
  },
  mixins: [userData],
  components: {
    TextArea,
    ErrorsAlert,
    InputCheck,
    BtnBack,
    BtnMain,
    BtngroupDouble,
    BtnSelect,
    BtnInput,
    InputAppend,
    InputgroupAppend,
    InputLabel,
    InputItem,
    InputGroup,
    InputPhone,
    TooltipsItem,
    BtnTooltip,
    BtnRadio,
    BtngroupRadio,
    TableData,
    TableHeader,
    TableRow,
    TableBody,
    TableBox,
    HeadingTwo,
    TableGroup,
    SubWizard,
    MainWizard,
    WizardGroup,
    HeadingOne
  },
  data () {
    return {
      errors: {},
      sexTypes: sexTypes,
      driverLicenseFlgs: driverLicenseFlgs,
      home_types: homeTypes,
      residence_years: residenceYears,
      marriage_statuses: marriageStatuses,
      number_of_children: numberOfChildren,
      showPinCode: 'true',
      userData: {
        name_kanji: '',
        name_kana: '',
        lastname_kana: '',
        lastname_kanji: '',
        firstname_kana: '',
        firstname_kanji: '',
        eva_kjn: '', //    漢字名
        eva_kjs: '', //    漢字姓
        eva_knn: '', //    カナ名
        eva_kns: '', //    カナ姓
        eva_birth: '',
        eva_sbt: '',
        bth: '',
        sex: '',
        phone_au_1: '',
        phone_au_2: '',
        phone_au_3: '',
        phone_mobile_1: '',
        phone_mobile_2: '',
        phone_mobile_3: '',
        ez_mail: '',
        mail_1: '',
        mail_2: '',
        loan_mail: '',
        loan_mail_confirmation: '',
        notice_flg: '',
        pincode: '',
        pincode_confirmation: '',
        driver_license_flg: '00',
        driver_license_num: '',
        postcode: '',
        address_region: '',
        address_city: '',
        address_street: '',
        address_building: '',
        address_kana: '',
        home_type: '00',
        nky_ngt: '',
        rent_loan_amount: '',
        marriage_status: '00',
        number_of_children: '00',
        eva_kddi_umu_kbn: '',
        eva_uq_admission_kbn: ''
      },
      hasPhoneAu: '',
      nkyNgtYyyy: '',
      nkyNgtMm: '',
      isDisabled: false,
      noticeFlg: '',
      hasSex: false,
      hasBth: true,
      inputItemsEn: inputItemsEn,
      trackingEventDataSetting: {}
    }
  },
  computed: {
    pinCodeInputType () {
      return this.showPinCode ? 'text' : 'password'
    },
    showNameKanjiLabel () {
      if (this.userData.eva_kjn.length === 0 || this.userData.eva_kjn.length > 13) {
        return false
      }
      if (this.userData.eva_kjs.length === 0 || this.userData.eva_kjs.length > 13) {
        return false
      }
      if ((this.userData.eva_kjn.length + this.userData.eva_kjs.length) > 14) {
        return false
      }
      if (this.userData.eva_kddi_umu_kbn === '0' && this.userData.eva_uq_admission_kbn === '0') {
        return false
      }
      if (this.userData.eva_kddi_umu_kbn === '0' && this.userData.eva_uq_admission_kbn === '1') {
        return false
      }
      return true
    },
    showNameKanaLabel () {
      if (this.userData.eva_knn.length === 0 || this.userData.eva_knn.length > 17) {
        return false
      }
      if (this.userData.eva_kns.length === 0 || this.userData.eva_kns.length > 17) {
        return false
      }
      if ((this.userData.eva_knn.length + this.userData.eva_kns.length) > 18) {
        return false
      }
      if (this.userData.eva_kddi_umu_kbn === '0' && this.userData.eva_uq_admission_kbn === '0') {
        return false
      }
      if (this.userData.eva_kddi_umu_kbn === '0' && this.userData.eva_uq_admission_kbn === '1') {
        return false
      }
      return true
    }
  },
  mounted () {
    try {
      getUserDataLambdaInvoke().then(res => {
        for (let key in res) {
          if (this.userData.hasOwnProperty(key)) {
            this.userData[key] = res[key].toString().trim()
          }
        }
        this.setHasSex()
        this.setHasBth()
        this.setHasPhoneAu()
        this.setHasPhoneMobile()
        this.initNoticeFlg(res)
        this.setPostParam()
        this.$store.dispatch('setSystemAuId', res.system_auid)
        this.registDeviceInfoData()
      }).catch(error => {
        this.goToErrorPage(error)
      })
    } catch (e) {
      this.goToErrorPage(e)
    }
    setTrackingEventDataSetting()
  },
  methods: {
    submitInputsProfile: function (event) {
      try {
        if (this.isDisabled) {
          return
        }
        this.isDisabled = true
        this.errors = {}
        this.setUserDataNoticeFlg()
        let orgDriverLicenseNum = this.userData.driver_license_num
        if (this.userData.driver_license_flg === '0') {
          this.userData.driver_license_num = ''
        }
        initCredentials().then(res => {
          validateProfileLambdaInvoke(this.userData).then(res => {
            if (res.redirect_page) {
              // 未成年の場合入会拒否へ遷移する
              let pageId = res.redirect_page.replace('-', '').toLowerCase()
              this.$router.push({name: pageId})
            } else {
              this.errors = res.errors || {}
              if (Object.keys(this.errors).length > 0) {
                this.userData.driver_license_num = orgDriverLicenseNum
                this.scrollTop()
                this.isDisabled = false
              } else {
                this.$router.push({name: 'en003'})
              }
            }
          }).catch(error => {
            this.goToErrorPage(error)
          })
        }).catch(error => {
          this.goToErrorPage(error)
        })
      } catch (e) {
        this.goToErrorPage(e)
      }
    },
    setUserDataNoticeFlg () {
      if (this.userData.notice_flg === null) {
        this.userData.notice_flg = ''
      }
    },
    initNoticeFlg (res) {
      if ('notice_flg' in res) {
      } else {
        this.userData.notice_flg = 'true'
      }
    },
    setPostParam () {
      if (this.showLoanMail) {
        delete this.userData['ez_mail']
        delete this.userData['mail_1']
        delete this.userData['mail_2']
      } else {
        delete this.userData['loan_mail']
        delete this.userData['loan_mail_confirmation']
      }
      if (this.hasPhoneAu) {
        delete this.userData['phone_mobile_1']
        delete this.userData['phone_mobile_2']
        delete this.userData['phone_mobile_3']
      } else {
        delete this.userData['phone_au_1']
        delete this.userData['phone_au_2']
        delete this.userData['phone_au_3']
      }
    },
    async registDeviceInfoData () {
      try {
        if (this.$store.getters.getSystemAuId) {
          let deviceInfoData = await getDeviceInfoData()
          registDeviceInfoDataLambdaInvoke(deviceInfoData).then(res => {
          }).catch(() => {})
        }
      } catch (e) {
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import "../assets/stylesheets/styles";

.furigana {
  font-family: inherit;
  @include mq-down(){
    height: 100px;
  }
  @include mq-up(){
    height: 60px;
  }
}

.wizard-list {
  background-color: red;
}

.flex {
  @include flex(flex,space-between,center,row,nowrap);
}

.list:first-child {
  @include mq-down(md) {
    width: 80%;
  }
}
.list:last-child {
  margin-right: 0;
  @include mq-down(md) {
    width: 90%;
  }
}

.table-row {
  &.-border {
    border-bottom: 1px solid #cccccc;
  }
}
.table-data {
    vertical-align: middle;
  >.inputgroup-append {
    &.name-box {
      align-items: flex-end;
      .input-group {
        &.name {
          display: flex;
          flex-wrap: wrap;
          .input-box {
            @include mq-down(md) {
              max-width: 50%;
              width: 100%;
            }
          }
        }
      }
    }
  }
  &.name-box {
    @include flex(flex,flex-start,center,row,wrap);
    width: 100%;
    border: none;
    @include mq-down(md) {
      align-items: flex-start;
      justify-content: space-between;
    }
    .warning {
      width: 100%;
    }
    .input-group {
      @include mq-down(){
        width: 50%;
        &:first-child {
          padding-right: 10px;
        }
      }
      &.name {
        @include mq-down(md) {
          margin-bottom: 15px;
        }
        @include mq-up(md) {
          /*max-width: 30%;*/
          /*width: 100%;*/
        }
      }
      .input-item {
        @include mq-down(md) {
          max-width: 130px;
          width: 100%;
        }
      }
      .input-append {
        min-width: 48px;
      }
    }
  }
}

.note {
  display: block;
  text-indent: -0.5em;
  margin-left: 0.5em;
  margin-bottom: 2px;
}

.important {
  color: $danger;
}

.pincode-txt {
  margin-bottom: 18px;
  @include mq-down(md) {
    line-height: 18px;
  }
  @include mq-up(md) {
    line-height: 24px;
  }
}

.pincode-flg {
  display: block;
  margin-bottom: 20px;
  line-height: 1;
}

.pincode-confirmation {
  @include mq-down(md) {
    margin-top: 5px;
  }
  @include mq-up(md) {
    margin-top: 15px;
  }
}

.pincode-confirmation-note {
  display: inline-block;
  margin-left: 6px;
}

.address-note {
  display: inline-block;
  @include mq-down(md) {
    margin-bottom: 6px;
  }
  @include mq-up(md) {
    margin-bottom: 15px;
  }
}
</style>



// WEBPACK FOOTER //
// src/pages/En002.vue